// Click-to-edit text cell.
//
// Click the cell → an <input> takes its place. Enter or blur commits;
// Esc reverts and cancels. While the parent's onSave promise is in
// flight the row shows a subtle "saving" state and the input is
// disabled. If the save throws, we revert to the original value and
// surface the error via the parent (which usually toasts it).
//
// Phase 3b only uses this for displayName. Tags / multi-value fields
// stay in the side-panel form.

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

interface EditableTextProps {
  value: string;
  onSave(next: string): Promise<void>;
  /** Optional renderer for the read-only state. Defaults to plain text. */
  display?: (value: string) => React.ReactNode;
  className?: string;
  ariaLabel: string;
  /** When true, click starts edit. Default true. */
  enabled?: boolean;
}

export function EditableText({
  value,
  onSave,
  display,
  className,
  ariaLabel,
  enabled = true
}: EditableTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  async function commit() {
    if (saving) return;
    const next = draft.trim();
    if (next === value || !next) {
      setEditing(false);
      setDraft(value);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      // Parent shows toast; revert local state.
      setDraft(value);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(value);
      setEditing(false);
    }
    e.stopPropagation();
  }

  if (!editing) {
    return (
      <div
        className={`min-w-0 ${enabled ? 'cursor-text' : ''} ${className ?? ''}`}
        onClick={(e) => {
          if (!enabled) return;
          e.stopPropagation();
          setEditing(true);
        }}
        title={enabled ? 'Click to edit' : undefined}
      >
        {display ? display(value) : <span className="truncate block">{value}</span>}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      disabled={saving}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={onKey}
      onClick={(e) => e.stopPropagation()}
      aria-label={ariaLabel}
      className="w-full rounded border border-input px-1 py-0.5 text-sm bg-[color:var(--cc-surface)] focus:outline-none focus:ring-2 focus:ring-[color:var(--cc-ring)]"
    />
  );
}
